import React, { useEffect, useContext, useState } from 'react';
import { Col, Row, Breadcrumb, Button } from 'react-bootstrap';
import axios from "axios";
import { ProductContext } from '../../ProductProvider';
import Preloader from '../../components/Preloader/Preloader';
import { Link } from "react-router-dom";
import { fetchAuthSession } from 'aws-amplify/auth';
import { useSnackbar } from 'notistack';
import logo from '../../assets/images/logo/COLLEGELOGO.jpeg';
import ProfileImage from '../../components/profile/ProfileImages';
import ReactToPrint from "react-to-print";
import { Helmet } from 'react-helmet-async';

const config = require('../../config.json');

export default function MyProfile() {
  const { loggedUser, validateLogin, myProfileImage } = useContext(ProductContext);
  const [profileDetails, setProfileDetails] = useState({
    applicantName: '', fatherName: '', dateOfBirth: '', gender: 'Male', nationality: '',
    religiousStatus: 'Scholastic', parishDiocese: '', congregationProvince: '', areaCode: '+91', mobileNumber: '', emailID: '', placeOfBirth: '',
    birthDistrict: '', birthState: '', localAddress: '', localState: '', localPincode: '', localContact: '', perAddress: '', perState: '', perPincode: '',
    perContactNo: '', courseCategory: 'Bachelor of Theology', degreeDetails: [], languageDetails: [], courseDetails: []
  });
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState('academic');
  const [profileImage, setProfileImage] = useState(logo);
  const { enqueueSnackbar } = useSnackbar();
  const [imageFile, setImageFile] = useState('');
  const componentRef = React.useRef(null);

  useEffect(() => {
    if (loggedUser.isAuthenticated) {
      fetchMyProfile();
    } else {
      validateLogin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedUser]);

  const fetchMyProfile = async () => {
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const searchTopic = "RegistrationID";
      const searchValue = loggedUser.nickname; // fallback to live student ID

      await axios.get(`${config.api.invokeURL}/dashboard/student/search?fetchKey=${searchValue}&fetchOption=${searchTopic}`, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      }).then(async (response) => {
        setProfileImage(myProfileImage || logo);
        if (response.data && response.data.length > 0) {
          setProfileDetails(response.data[0]);
        }
        enqueueSnackbar("Student Profile loaded successfully!", { variant: 'success' });
        setLoaded(true);
      }).catch((error) => {
        enqueueSnackbar("Error loading profile: " + error.message, { variant: 'warning' });
        setLoaded(true);
      });
    } catch (error) {
      enqueueSnackbar("Error: " + error.message, { variant: 'warning' });
      setLoaded(true);
    }
  };

  const copyToClipboard = (text, label) => {
    if (navigator && navigator.clipboard) {
      navigator.clipboard.writeText(text);
      enqueueSnackbar(`Copied ${label} to clipboard!`, { variant: 'success' });
    }
  };

  const reactToPrintContent = React.useCallback(() => {
    return componentRef.current;
  }, []);

  const reactToPrintTrigger = React.useCallback(() => {
    return (
      <Button variant="outline-primary" size="sm" className="d-inline-flex align-items-center gap-2 px-3 py-2 fw-semibold rounded-pill">
        <i className="fa fa-print"></i> Print Bio-Data
      </Button>
    );
  }, []);

  const studentRegId = profileDetails.registrationNo || loggedUser.nickname || '26LTH1006';

  return (
    <div className="my-profile-page animate-fade-in-up">
      <Helmet>
        <title>Student Profile - Vidyajyoti College of Theology</title>
        <meta name="description" content="View and manage personal theological student profile" />
        <link rel="canonical" href="/profile" />
      </Helmet>

      <Preloader show={!loaded} />

      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item>
          <Link to="/">
            <i className="fa fa-home" aria-hidden="true"></i> Dashboard
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Students</Breadcrumb.Item>
        <Breadcrumb.Item active>Profile Hub</Breadcrumb.Item>
      </Breadcrumb>

      {/* Page Header */}
      <div className="page-title-section">
        <div className="page-title-wrapper">
          <h4>
            <i className="fa fa-id-card-o text-primary"></i> Student Profile Hub
          </h4>
          <div className="page-subtitle">
            Academic Session 2025–2026 • Registration ID: <span className="fw-bold text-primary">{studentRegId}</span>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <ReactToPrint
            content={reactToPrintContent}
            documentTitle={`Student_Profile_${profileDetails.applicantName || 'Vidyajyoti'}`}
            trigger={reactToPrintTrigger}
          />
        </div>
      </div>

      <div ref={componentRef}>
        {/* Modern Hero Identity Banner (Light & Elegant Theme) */}
        <div className="student-hero-banner mb-4">
          <Row className="align-items-center g-4">
            <Col xs={12} md={3} className="text-center">
              <ProfileImage
                formType="View"
                profileImage={profileImage}
                setImageFile={setImageFile}
              />
            </Col>

            <Col xs={12} md={6} className="text-center text-md-start">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2 justify-content-center justify-content-md-start">
                <span className="badge primary">
                  <i className="fa fa-graduation-cap me-1"></i>
                  {profileDetails.courseCategory || 'Bachelor of Theology'}
                </span>
                <span className="badge" style={{ background: 'rgba(37, 99, 235, 0.08)', color: 'var(--main-color)', border: '1px solid rgba(37, 99, 235, 0.2)' }}>
                  <i className="fa fa-shield me-1"></i>
                  {profileDetails.religiousStatus || 'Scholastic'}
                </span>
                <span className="badge bg-success text-white">
                  <i className="fa fa-check-circle me-1"></i>
                  Verified Scholar
                </span>
              </div>

              <h2 className="fw-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '-0.02em', color: 'var(--txt-color)' }}>
                {profileDetails.applicantName || loggedUser.name || 'STUDENT NAME'}
              </h2>

              <div className="d-flex flex-wrap align-items-center gap-2 mb-3 justify-content-center justify-content-md-start">
                <button
                  type="button"
                  className="copy-chip"
                  onClick={() => copyToClipboard(studentRegId, 'Registration ID')}
                  title="Click to copy Registration ID"
                >
                  <i className="fa fa-id-badge text-primary"></i>
                  <span>ID: <strong>{studentRegId}</strong></span>
                  <i className="fa fa-clone ms-1 text-muted" style={{ fontSize: '0.75rem' }}></i>
                </button>

                {profileDetails.emailID && (
                  <button
                    type="button"
                    className="copy-chip"
                    onClick={() => copyToClipboard(profileDetails.emailID, 'Email Address')}
                    title="Click to copy Email"
                  >
                    <i className="fa fa-envelope text-primary"></i>
                    <span>{profileDetails.emailID}</span>
                    <i className="fa fa-clone ms-1 text-muted" style={{ fontSize: '0.75rem' }}></i>
                  </button>
                )}
              </div>

              <div className="text-muted small d-flex align-items-center gap-2 justify-content-center justify-content-md-start">
                <i className="fa fa-university text-primary"></i>
                <span>
                  {profileDetails.congregationProvince || 'Jesuit Province'} • {profileDetails.parishDiocese || 'Diocese of Origin'}
                </span>
              </div>
            </Col>

            {/*<Col xs={12} md={3}>
              <div className="digital-id-badge text-center">
                <img
                  src={logo}
                  alt="College Crest"
                  width="52"
                  height="52"
                  className="rounded-circle mb-2 shadow-sm"
                  style={{ border: '2px solid var(--main-color)', objectFit: 'cover' }}
                />
                <div className="fw-bold" style={{ fontSize: '0.88rem', color: 'var(--txt-color)' }}>
                  VIDYAJYOTI COLLEGE
                </div>
                <div className="text-muted" style={{ fontSize: '0.72rem', letterSpacing: '0.04em' }}>
                  DELHI • THEOLOGY
                </div>
                <div className="mt-2 pt-2 border-top small text-primary fw-bold">
                  {studentRegId}
                </div>
              </div>
            </Col>*/}
          </Row>
        </div>

        {/* 4 Quick Stat Overview Cards */}
        <Row className="g-3 mb-4">
          <Col xs={12} sm={6} lg={3}>
            <div className="profile-stat-box">
              <div className="profile-stat-icon bg-primary-soft text-primary">
                <i className="fa fa-graduation-cap"></i>
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Program</div>
                <div className="fw-bold" style={{ color: 'var(--txt-color)' }}>{profileDetails.courseCategory || 'B.Th'}</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <div className="profile-stat-box">
              <div className="profile-stat-icon bg-warning-soft text-warning">
                <i className="fa fa-id-badge"></i>
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Student ID</div>
                <div className="fw-bold" style={{ color: 'var(--txt-color)' }}>{studentRegId}</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <div className="profile-stat-box">
              <div className="profile-stat-icon bg-info-soft text-info">
                <i className="fa fa-shield"></i>
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Status</div>
                <div className="fw-bold" style={{ color: 'var(--txt-color)' }}>{profileDetails.religiousStatus || 'Scholastic'}</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={6} lg={3}>
            <div className="profile-stat-box">
              <div className="profile-stat-icon bg-success-soft text-success">
                <i className="fa fa-check-circle"></i>
              </div>
              <div>
                <div className="text-muted small fw-bold text-uppercase">Academic State</div>
                <div className="fw-bold text-success">Active Scholar</div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Interactive Tab Navigation */}
        <div className="profile-nav-pills">
          <button
            type="button"
            className={`profile-nav-pill-btn ${activeTab === 'academic' ? 'active' : ''}`}
            onClick={() => setActiveTab('academic')}
          >
            <i className="fa fa-graduation-cap"></i>
            <span>Academic Details</span>
          </button>

          <button
            type="button"
            className={`profile-nav-pill-btn ${activeTab === 'personal' ? 'active' : ''}`}
            onClick={() => setActiveTab('personal')}
          >
            <i className="fa fa-user"></i>
            <span>Personal Bio-Data</span>
          </button>

          <button
            type="button"
            className={`profile-nav-pill-btn ${activeTab === 'ecclesiastical' ? 'active' : ''}`}
            onClick={() => setActiveTab('ecclesiastical')}
          >
            <i className="fa fa-university"></i>
            <span>Ecclesiastical & Diocese</span>
          </button>

          <button
            type="button"
            className={`profile-nav-pill-btn ${activeTab === 'contact' ? 'active' : ''}`}
            onClick={() => setActiveTab('contact')}
          >
            <i className="fa fa-map-marker"></i>
            <span>Addresses & Contact</span>
          </button>
        </div>

        {/* Tab Content 1: Academic Details */}
        {activeTab === 'academic' && (
          <div className="profile-section-card animate-fade-in">
            <div className="profile-section-header">
              <div className="action-btn-circle" style={{ width: '42px', height: '42px', background: 'rgba(37, 99, 235, 0.1)', color: 'var(--main-color)' }}>
                <i className="fa fa-graduation-cap fa-lg"></i>
              </div>
              <div>
                <h5>Academic Program & Institutional Enrollment</h5>
                <div className="text-muted small">Course designation, faculty affiliation, and registration details</div>
              </div>
            </div>

            <Row className="g-3">
              <Col xs={12} md={6}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-book text-primary"></i> Degree Program
                  </div>
                  <div className="profile-info-value text-primary fs-6">
                    {profileDetails.courseCategory || 'Bachelor of Theology (B.Th)'}
                  </div>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-id-card text-primary"></i> Registration Number
                  </div>
                  <div className="profile-info-value fw-bold">
                    {studentRegId}
                  </div>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-institution text-primary"></i> Faculty & College
                  </div>
                  <div className="profile-info-value">
                    Faculty of Theology • Vidyajyoti College of Theology (Delhi)
                  </div>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-check-circle text-success"></i> Academic Enrollment Status
                  </div>
                  <div className="profile-info-value text-success d-flex align-items-center gap-2">
                    <span className="badge bg-success text-white">Active</span>
                    <span>Regular Full-Time Theological Student</span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Tab Content 2: Personal Bio-Data */}
        {activeTab === 'personal' && (
          <div className="profile-section-card animate-fade-in">
            <div className="profile-section-header">
              <div className="action-btn-circle" style={{ width: '42px', height: '42px', background: 'rgba(37, 99, 235, 0.1)', color: 'var(--main-color)' }}>
                <i className="fa fa-user fa-lg"></i>
              </div>
              <div>
                <h5>Personal Bio-Data & Identification</h5>
                <div className="text-muted small">Official student identity and demographic records</div>
              </div>
            </div>

            <Row className="g-3">
              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-user text-primary"></i> Student Full Name
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.applicantName || loggedUser.name || 'N/A'}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-users text-primary"></i> Father / Guardian Name
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.fatherName || 'N/A'}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-calendar text-primary"></i> Date of Birth
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.dateOfBirth || 'N/A'}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-venus-mars text-primary"></i> Gender
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.gender || 'Male'}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-globe text-primary"></i> Nationality
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.nationality || 'INDIAN'}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-map-pin text-primary"></i> Place of Birth
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.placeOfBirth || 'N/A'} {profileDetails.birthDistrict ? `(${profileDetails.birthDistrict})` : ''}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-map text-primary"></i> Birth District
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.birthDistrict || 'N/A'}
                  </div>
                </div>
              </Col>

              <Col xs={12} sm={6} lg={4}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-compass text-primary"></i> Birth State
                  </div>
                  <div className="profile-info-value">
                    {profileDetails.birthState || 'N/A'}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Tab Content 3: Ecclesiastical & Religious Affiliation */}
        {activeTab === 'ecclesiastical' && (
          <div className="profile-section-card animate-fade-in">
            <div className="profile-section-header">
              <div className="action-btn-circle" style={{ width: '42px', height: '42px', background: 'rgba(37, 99, 235, 0.1)', color: 'var(--main-color)' }}>
                <i className="fa fa-university fa-lg"></i>
              </div>
              <div>
                <h5>Ecclesiastical & Religious Affiliation</h5>
                <div className="text-muted small">Canonical status, Diocese of origin, and Congregation/Province</div>
              </div>
            </div>

            <Row className="g-4">
              <Col xs={12} md={4}>
                <div className="profile-info-tile p-4 text-center">
                  <div className="profile-info-label justify-content-center">
                    <i className="fa fa-shield text-primary"></i> Religious Status
                  </div>
                  <div className="mt-2">
                    <span className="badge primary" style={{ fontSize: '1rem', padding: '8px 18px' }}>
                      {profileDetails.religiousStatus || 'Scholastic'}
                    </span>
                  </div>
                  <div className="text-muted small mt-2">Canonical Theological Standing</div>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="profile-info-tile p-4">
                  <div className="profile-info-label">
                    <i className="fa fa-church text-primary"></i> Parish & Diocese of Origin
                  </div>
                  <div className="profile-info-value fs-6 mt-1">
                    {profileDetails.parishDiocese || 'N/A'}
                  </div>
                  <div className="text-muted small mt-2">Ecclesiastical Jurisdictional Region</div>
                </div>
              </Col>

              <Col xs={12} md={4}>
                <div className="profile-info-tile p-4">
                  <div className="profile-info-label">
                    <i className="fa fa-sitemap text-primary"></i> Congregation & Province
                  </div>
                  <div className="profile-info-value fs-6 mt-1">
                    {profileDetails.congregationProvince || 'N/A'}
                  </div>
                  <div className="text-muted small mt-2">Religious Institute / Jesuit Province</div>
                </div>
              </Col>
            </Row>
          </div>
        )}

        {/* Tab Content 4: Addresses & Contact */}
        {activeTab === 'contact' && (
          <div className="profile-section-card animate-fade-in">
            <div className="profile-section-header">
              <div className="action-btn-circle" style={{ width: '42px', height: '42px', background: 'rgba(37, 99, 235, 0.1)', color: 'var(--main-color)' }}>
                <i className="fa fa-map-marker fa-lg"></i>
              </div>
              <div>
                <h5>Contact Information & Residential Addresses</h5>
                <div className="text-muted small">Direct communication channels and verified residential coordinates</div>
              </div>
            </div>

            {/* Quick Contact Tiles */}
            <Row className="g-3 mb-4">
              <Col xs={12} md={6}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-envelope text-primary"></i> Registered Email ID
                  </div>
                  <div className="profile-info-value d-flex justify-content-between align-items-center">
                    <a href={`mailto:${profileDetails.emailID}`} className="text-primary text-decoration-none">
                      {profileDetails.emailID || 'N/A'}
                    </a>
                    {profileDetails.emailID && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 px-2 rounded"
                        onClick={() => copyToClipboard(profileDetails.emailID, 'Email')}
                        title="Copy Email"
                      >
                        <i className="fa fa-clone"></i>
                      </button>
                    )}
                  </div>
                </div>
              </Col>

              <Col xs={12} md={6}>
                <div className="profile-info-tile">
                  <div className="profile-info-label">
                    <i className="fa fa-phone text-primary"></i> Mobile Contact Number
                  </div>
                  <div className="profile-info-value d-flex justify-content-between align-items-center">
                    <span>
                      {profileDetails.mobileNumber ? `${profileDetails.areaCode || '+91'} ${profileDetails.mobileNumber}` : 'N/A'}
                    </span>
                    {profileDetails.mobileNumber && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary py-0 px-2 rounded"
                        onClick={() => copyToClipboard(`${profileDetails.areaCode || '+91'} ${profileDetails.mobileNumber}`, 'Phone')}
                        title="Copy Phone"
                      >
                        <i className="fa fa-clone"></i>
                      </button>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Dual Location Grid */}
            <Row className="g-4">
              <Col xs={12} lg={6}>
                <div className="profile-address-card">
                  <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                    <i className="fa fa-home text-primary" style={{ fontSize: '1.3rem' }}></i>
                    <h6 className="fw-bold mb-0" style={{ color: 'var(--txt-color)' }}>Local Residence / College House</h6>
                  </div>

                  <div className="mb-3">
                    <div className="text-muted small text-uppercase fw-bold">Street Address</div>
                    <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.localAddress || 'N/A'}</div>
                  </div>

                  <Row className="g-2">
                    <Col xs={6}>
                      <div className="text-muted small text-uppercase fw-bold">State</div>
                      <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.localState || 'N/A'}</div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted small text-uppercase fw-bold">Pincode</div>
                      <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.localPincode || 'N/A'}</div>
                    </Col>
                    <Col xs={12} className="mt-2">
                      <div className="text-muted small text-uppercase fw-bold">Contact Number</div>
                      <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.localContact || 'N/A'}</div>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12} lg={6}>
                <div className="profile-address-card">
                  <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
                    <i className="fa fa-building text-primary" style={{ fontSize: '1.3rem' }}></i>
                    <h6 className="fw-bold mb-0" style={{ color: 'var(--txt-color)' }}>Permanent / Home Address</h6>
                  </div>

                  <div className="mb-3">
                    <div className="text-muted small text-uppercase fw-bold">Street Address</div>
                    <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.perAddress || 'N/A'}</div>
                  </div>

                  <Row className="g-2">
                    <Col xs={6}>
                      <div className="text-muted small text-uppercase fw-bold">State</div>
                      <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.perState || 'N/A'}</div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-muted small text-uppercase fw-bold">Pincode</div>
                      <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.perPincode || 'N/A'}</div>
                    </Col>
                    <Col xs={12} className="mt-2">
                      <div className="text-muted small text-uppercase fw-bold">Contact Number</div>
                      <div className="fw-semibold" style={{ color: 'var(--txt-color)' }}>{profileDetails.perContactNo || 'N/A'}</div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
}
